import { type RefObject, useEffect, useRef } from 'react';

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean) {
    const previousFocusRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!active || !containerRef.current) return;

        previousFocusRef.current = document.activeElement as HTMLElement;

        const container = containerRef.current;
        const focusables = () => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));

        // Focus first focusable element
        const first = focusables()[0];
        first?.focus();

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;

            const nodes = focusables();
            if (nodes.length === 0) return;

            const firstNode = nodes[0];
            const lastNode = nodes[nodes.length - 1];

            if (e.shiftKey && document.activeElement === firstNode) {
                e.preventDefault();
                lastNode.focus();
            } else if (!e.shiftKey && document.activeElement === lastNode) {
                e.preventDefault();
                firstNode.focus();
            }
        };

        container.addEventListener('keydown', handleKeyDown);

        return () => {
            container.removeEventListener('keydown', handleKeyDown);
            previousFocusRef.current?.focus();
        };
    }, [active, containerRef]);
}
